import React, {useEffect, useMemo, useState} from "react";
import {Button, Checkbox, Col, Dropdown, Menu, Modal, Pagination, Row, Space} from "antd";
import {DownOutlined, EditOutlined, PercentageOutlined, SearchOutlined, WarningOutlined} from '@ant-design/icons';
import {formatNumber} from "@lib/format";
import {DiamondIcon, DollarIcon, FlameIcon, GridIcon, InfoIcon, KeyIcon, ListIcon} from "@components/icons/Icons";
import {IssueFilters, IssueFiltersPaginatedQuery} from "@mwServices/Issue";
import {useCollectionRemoveIssuesMutation, useGetCollectionIssuesQuery} from "@mwServices/User";
import {IssueGrid} from "@components/grid";
import {ClearFiltersBar} from "@components/issues-filter-drawer";
import MyCollectionFiltersDrawer from "@mwPages/my-collection/issues/MyCollectionFiltersDrawer";
import MyCollectionGridItem from "@mwPages/my-collection/issues/MyCollectionGridItem";
import MyCollectionListItem from "@mwPages/my-collection/issues/MyCollectionListItem";
import {SortIcon} from "@components/sort-icon";
import {PAGINATION_PAGES} from "@components/grid/Grid";
import {Box, useBoxesAddIssuesMutation, useBoxesRemoveIssuesMutation, useGetBoxesQuery} from "@mwServices/Boxes";
import {showResponse} from "@services/Api";
import AssignConditionModal from "@components/assign-condition-modal";
import AssignLocationModal from "@components/assign-location-modal";
import MarkAsSoldDrawer from "@components/mark-as-sold-drawer";
import usePreferences from "@hooks/usePreferences";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {DebounceInput} from "@components/debounce-input";
import {scrollToThen} from "@lib/utils";
import AssignPurchasePriceModal from "@components/assign-purchase-price-modal";
import {Series} from "@mwServices/Series";
import {showInfoModal} from "@lib/showModal";
import {useWindowSize} from "@components/window-size-provider";
import useUserBoxes from "@hooks/useUserBoxes";
import {AddEditComicDrawer} from "@components/add-edit-comic-drawer";

interface MyCollectionIssuesProps {
    search?: string;
    setSearch?: (search: string) => void;
    setTotalIssues?: (total: number) => void;
    setAddBox?: ({}: {show: boolean, issue_ids: number[], series_ids: number[]}) => void;
    showSeries?: Series;
    onSetBoxImage?: (issueId: number) => void;
    showBox?: Box;
    searchPlaceholder?: string;
    issueFilters?: IssueFilters;
    setIssueFilters?: (filters: IssueFilters | null) => void;
    showSearchIssue?: boolean;
    collectionOverviewInfo?: any;
}

const GainsToggle = ({ isDollar, setIsDollar }) => {
    return (
        <Button
            icon={isDollar ? (<DollarIcon />) : (<PercentageOutlined />)}
            size="small"
            shape={"circle"}
            className={"greyButton mar-left-5"}
            onClick={() => setIsDollar(!isDollar)}
        />
    );
};

const MyCollectionIssues = ({
    search,
    setSearch,
    setTotalIssues,
    setAddBox,
    showSeries,
    onSetBoxImage,
    showBox,
    searchPlaceholder,
    issueFilters,
    setIssueFilters,
    showSearchIssue,
    collectionOverviewInfo
}: MyCollectionIssuesProps) => {
    const windowDimensions = useWindowSize();
    const [expandedIssue, setExpandedIssue] = useState(null);
    const [editIssue, setEditIssue] = useState(null);
    const { showListView, setShowListView, itemCount, setItemCount } = usePreferences();

    const defaultFilters = useMemo(() => ({
        query: '',
        showVariants: true,
        groupByGrade: false,
        isHot: false,
        isKey: false,
        isRare: false,
        sort: 0,
        series: showSeries ? [showSeries.series_id] : undefined,
        boxes: showBox ? [showBox.id] : undefined,
        multipleDateRanges: undefined,
        formats: undefined,
        locations: undefined,
        gradedBy: undefined,
        grades: undefined,
        publishers: undefined,
        showValuesShown: undefined,
        getItSlabbed: undefined,
        isSigned: undefined,
    }), []);
    const [issueParams, setIssueParams] = useState<IssueFiltersPaginatedQuery>({
        filters: { ...defaultFilters },
        page: 1,
        perPage: itemCount,
    });
    const issueQuery = useGetCollectionIssuesQuery(issueParams);
    const pagination = issueQuery && issueQuery.data && issueQuery.data.pagination ? issueQuery.data.pagination : null;
    const [showEditMultiple, setShowEditMultiple] = useState(false);
    const [selectedComicIds, setSelectedComicIds] = useState<number[]>([]);
    const [showAssignConditionModal, setShowAssignConditionModal] = useState(false);
    const [showAssignLocationModal, setShowAssignLocationModal] = useState(false);
    const [showAssignPurchasePriceModal, setShowAssignPurchasePriceModal] = useState(false);
    const [showMarkAsSold, setShowMarkAsSold] = useState(false);
    const boxesQuery = useGetBoxesQuery({ filters: {} });
    const [postAddIssuesToBox, addIssuesToBoxResponse] = useBoxesAddIssuesMutation();
    const [postRemoveIssues, removeIssuesResponse] = useCollectionRemoveIssuesMutation();
    const [callRemoveFromBox, removeFromBoxResponse] = useBoxesRemoveIssuesMutation();
    const [showSearch, setShowSearch] = useState(false);
    const [showDollarGains, setShowDollarGains] = useState(true);
    const { getBoxName, removeIssueFromBox } = useUserBoxes();

    const subscriptionType = useSubscriptionType();
    const allowUnlimited = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const showValueRibbon = useMemo(() => (subscriptionType.isTrial || subscriptionType.isStandard || subscriptionType.isUnlimited), [subscriptionType]);

    const selectedIssues = useMemo(() => {
        if (issueQuery.data && issueQuery.data.issues) {
            return issueQuery.data.issues.filter(issue => issue.collection_item_id && selectedComicIds.indexOf(issue.collection_item_id) !== -1);
        } else {
            return [];
        }
    }, [selectedComicIds]);

    useEffect(() => {
        showResponse(addIssuesToBoxResponse, 'Issues added to box.', 'Sorry, there was a problem adding the issues to the Box.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addIssuesToBoxResponse]);

    useEffect(() => {
        showResponse(removeIssuesResponse, 'Issues removed from collection.', 'Sorry, there was a problem removing the issues from the collection.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeIssuesResponse]);

    useEffect(() => {
        showResponse(removeFromBoxResponse, 'Comics removed from box.', 'Failed to remove comics from box.');
    }, [removeFromBoxResponse]);

    useEffect(() => {
        setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, query: search ? search : '' } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        if (issueFilters) setIssueParams({ ...issueParams, page: 1, filters: { ...defaultFilters, ...issueFilters } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issueFilters]);

    useEffect(() => {
        setSelectedComicIds([]);

        if(issueQuery.data && issueQuery.data.limit_hit){
            Modal.confirm({
                autoFocusButton: null,
                icon: <WarningOutlined />,
                content: (
                    <div>
                        <div><b>Max Quantity Reached</b></div>
                        <div className="pad-top-10">
                            Sorry, you're only allowed {issueQuery.data.limit} comics with the {subscriptionType.subscriptionType} account.<br/>
                            You are currently over your limit. Your search results will be limited to {issueQuery.data.limit} comics until you upgrade.<br/>
                        </div>
                    </div>
                ),
                okText: "UPGRADE",
                onOk() {
                    window.location.href = "/plans/unlimited/";
                },
            });
        }

        if (setTotalIssues) setTotalIssues(issueQuery.data ? issueQuery.data.overview.total : 0);
    }, [issueQuery.data, setTotalIssues]);

    useEffect(() => {
        if (issueParams.filters.sort === 9 && !showDollarGains) {
            setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort: 3 } });
        }
        else if (issueParams.filters.sort === 3 && showDollarGains) {
            setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort: 9 } });
        }
        else if (issueParams.filters.sort === 15 && !showDollarGains) {
            setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort: 18 } });
        }
        else if (issueParams.filters.sort === 18 && showDollarGains) {
            setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort: 15 } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDollarGains]);

    const onIssueClick = (issue) => {
        if (issue.total_in_grade > 1) {
            setExpandedIssue(issue === expandedIssue ? null : issue);
        } else {
            setEditIssue(issue);
        }
    };

    const onGainLossInfoClick = () => {
        showInfoModal({
            title: <b>How does Gain/Loss work?</b>,
            content: (
                <>
                    <p>
                        <b>COVR</b>PRICE’s Gain/Loss calculations are available on any comic in your collection that has a purchase price entered. With a purchase price we can compare that to our calculated FMV and give you a % change and $ change allowing us to show you how well each of your purchases are performing.
                    </p>
                    <p>
                        To switch from % to $, tap the icon in the column's header. This will toggle between the two options.
                    </p>
                </>
            ),
            onOk() { },
        });
    };

    const removeIssuesFromBox = async () => {
        if (showBox) {
            let sanitisedData = {
                issue_ids: selectedComicIds,
                box_id: Number(showBox.id),
            };

            await callRemoveFromBox(sanitisedData);
        }
    };

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const smallButtons = useMemo(() => {
        return windowDimensions.under(1200);
    }, [windowDimensions]);

    const showOverviewInfo = useMemo(() => {
        if(showBox) return showBox.overview;
        if(showSeries) return showSeries.overview;
        return collectionOverviewInfo;
    }, [collectionOverviewInfo, showBox, showSeries]);

    const toggleSelected = (id: number) => {
        setSelectedComicIds(currentIds => {
            if(currentIds.indexOf(id) === -1) {
                return [...currentIds, id];
            } else {
                return currentIds.filter((item) => item !== id);
            }
        })
    };

    const setShowMarkAsSoldWithIssue = (id: number) => {
        setSelectedComicIds([id]);
        setShowMarkAsSold(true);
    };

    const setShowAssignPurchasePriceModalWithIssue = (id: number) => {
        setSelectedComicIds([id]);
        setShowAssignPurchasePriceModal(true);
    };

    return (
        <>
            <Row className="my-collection-issues-container">
                <Col>
                    <Space>
                        {!windowDimensions.isSmallScreen && (
                            <>
                                <Button icon={<GridIcon />} size="large" className={"greyButton" + (showListView ? '' : ' selected')} onClick={() => setShowListView(false)} />
                                <Button icon={<ListIcon />} size="large" className={"greyButton" + (showListView ? ' selected' : '')} onClick={() => setShowListView(true)} />
                            </>
                        )}
                        <Button className={"greyButton " + (showEditMultiple ? ' selectedBlue' : '')} size={"large"} onClick={() => setShowEditMultiple(!showEditMultiple)} >
                            <span className="filterButtonText">Edit</span>
                            <EditOutlined className="font-12" />
                        </Button>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        {windowDimensions.isLargeScreen && (
                            <>
                                <Button
                                    icon={<div className="valueBadge hotIssue isActive mar-right-5"><FlameIcon /></div>}
                                    size="large"
                                    className={"greyButton " + (issueParams.filters.isHot ? ' selected' : '')}
                                    onClick={() => setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        filters: { ...issueParams.filters,
                                            isHot: !issueParams.filters.isHot,
                                            isKey: false,
                                            isRare: false
                                        }
                                    })}
                                >
                                    {showOverviewInfo ? formatNumber(showOverviewInfo.items_hot) : "0"}
                                </Button>
                                <Button
                                    icon={<div className="valueBadge keyIssue isActive mar-right-5" ><KeyIcon /></div>}
                                    size="large"
                                    className={"greyButton " + (issueParams.filters.isKey ? ' selected' : '')}
                                    onClick={() => setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        filters: { ...issueParams.filters,
                                            isHot: false,
                                            isKey: !issueParams.filters.isKey,
                                            isRare: false
                                        }
                                    })}
                                >
                                    {showOverviewInfo ? formatNumber(showOverviewInfo.items_key) : "0"}
                                </Button>
                                <Button
                                    icon={<div className="valueBadge rareIssue isActive mar-right-5" ><DiamondIcon /></div>}
                                    size="large"
                                    className={"greyButton " + (issueParams.filters.isRare ? ' selected' : '')}
                                    onClick={() => setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        filters: { ...issueParams.filters,
                                            isHot: false,
                                            isKey: false,
                                            isRare: !issueParams.filters.isRare,
                                        }
                                    })}
                                >
                                    {showOverviewInfo ? formatNumber(showOverviewInfo.items_rare) : "0"}
                                </Button>
                            </>
                        )}


                        {!windowDimensions.isLargeScreen && (
                            <Button className="greyButton " size="large" onClick={() => setShowSearch(!showSearch)}>
                                <SearchOutlined />
                            </Button>
                        )}

                        {windowDimensions.isLargeScreen && showSearchIssue && (
                            <DebounceInput
                                size="large"
                                placeholder="Issue #"
                                className="searchIssueInput"
                                prefix={<SearchOutlined />}
                                maxLength={100}
                                value={search}
                                debounceTimeout={2000}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    if (setSearch && (value !== search)) setSearch(value);
                                }}
                            />
                        )}

                        <MyCollectionFiltersDrawer
                            showSeries={showSeries}
                            showBox={showBox}
                            filters={issueParams.filters}
                            setFilters={(filters) => setIssueParams({...issueParams, page: 1, filters})}
                            showOverviewInfo={showOverviewInfo}
                        />

                        {!windowDimensions.isSmallScreen && pagination && (
                            <Pagination
                                current={pagination.current_page}
                                pageSize={pagination.per_page}
                                total={pagination.total_items}
                                onChange={(page) => {
                                    if (issueParams && issueParams.page !== page) {
                                        setIssueParams({ ...issueParams, page: page });
                                    }
                                }}
                                simple
                            />
                        )}
                    </Space>
                </Col>
            </Row>

            {windowDimensions.isSmallScreen && (
                <Row className="my-collection-issues-container top-line" >

                    <Col>
                        <Space>
                            <Button icon={<GridIcon />} size="large" className={"greyButton" + (showListView ? '' : ' selected')} onClick={() => setShowListView(false)} />
                            <Button icon={<ListIcon />} size="large" className={"greyButton" + (showListView ? ' selected' : '')} onClick={() => setShowListView(true)} />
                        </Space>
                    </Col>

                    <Col className="flex flex-vertical-center">
                        <Space>
                            {pagination && (
                                <Pagination
                                    current={pagination.current_page}
                                    pageSize={pagination.per_page}
                                    total={pagination.total_items}
                                    onChange={(page) => {
                                        if (issueParams && issueParams.page !== page) {
                                            setIssueParams({ ...issueParams, page: page });
                                        }
                                    }}
                                    simple
                                />
                            )}
                        </Space>
                    </Col>
                </Row>
            )}


            {showSearch && (
                <Row className="my-collection-issues-container top-line" >
                    <DebounceInput
                        placeholder={searchPlaceholder}
                        className="searchInput"
                        prefix={<SearchOutlined />}
                        value={search}
                        maxLength={100}
                        debounceTimeout={2000}
                        onChange={(event) => {
                            const { value } = event.target;
                            if (setSearch && (value !== search)) setSearch(value);
                        }}
                    />
                </Row>
            )}

            {showEditMultiple && (
                <Row className={'edit-multiple-row ' + (windowDimensions.isLargeScreen ? 'large-screen ' : '')}>
                    <Col span={24} className="edit-multiple-col">
                        <Checkbox
                            checked={issueQuery.data && selectedComicIds.length === issueQuery.data.issues.length}
                            onChange={() => {
                                if (issueQuery.data) {
                                    if (selectedComicIds.length === issueQuery.data.issues.length) {
                                        setSelectedComicIds([]);
                                    } else {
                                        setSelectedComicIds(issueQuery.data.issues.map(issue => issue.collection_item_id) as number[]);
                                    }
                                }
                            }}
                        />

                        {allowUnlimited && (
                            <Dropdown
                                trigger={["click"]}
                                className="greyButton selectButton"
                                disabled={selectedComicIds.length === 0}
                                overlay={(
                                    <Menu >
                                        {boxesQuery.data && boxesQuery.data.boxes.map((box) => (
                                            <Menu.Item key={box.id} onClick={() => postAddIssuesToBox({ box_id: box.id, issue_ids: selectedComicIds })}>{box.name}</Menu.Item>
                                        ))}
                                        <Menu.Item key={0} onClick={() => { if(setAddBox) setAddBox({show: true, issue_ids: selectedComicIds, series_ids: []})}}>+ Create new Virtual Box</Menu.Item>
                                    </Menu>
                                )}
                            >
                                <Button
                                    className="greyButton mar-top-5 edit-multi-item"
                                    size={windowDimensions.isLargeScreen ? "large" : 'middle'} >
                                    Add to Virtual Box
                                    <DownOutlined />
                                </Button>
                            </Dropdown>
                        )}

                        <Button
                            className="greyButton mar-top-5 edit-multi-item"
                            size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                            disabled={selectedComicIds.length === 0}
                            onClick={() => { setShowAssignLocationModal(true)}}
                        >
                            Add Location
                        </Button>

                        <Button
                            className="greyButton mar-top-5 edit-multi-item"
                            size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                            disabled={selectedComicIds.length === 0}
                            onClick={() => Modal.confirm({
                                autoFocusButton: null,
                                icon: <WarningOutlined />,
                                content: (<div>
                                    <div><b>Delete Items</b></div>
                                    <div className="pad-top-10">
                                        Are you sure?<br />
                                        Selecting this option will permanently delete these items.
                                    </div>
                                </div>),
                                onOk() {
                                    postRemoveIssues({ issue_ids: selectedComicIds });
                                },
                            })}
                        >
                            Delete
                        </Button>

                        {!!showBox && (
                            <Button
                                style={{ marginLeft: windowDimensions.isLargeScreen ? 20 : 10, marginTop: 5 }}
                                className="greyButton "
                                size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                                disabled={selectedComicIds.length === 0}
                                onClick={() => Modal.confirm({
                                    autoFocusButton: null,
                                    icon: <WarningOutlined />,
                                    content: (<div>
                                        <div><b>Remove Items From Box</b></div>
                                        <div style={{paddingTop: 10}}>
                                            Are you sure?<br/>
                                            Selecting this option will remove these comics from this Virtual Box. The comics will remain in your collection.
                                        </div>
                                    </div>),
                                    onOk() {
                                        removeIssuesFromBox();
                                    },
                                })}
                            >
                                Remove From Virtual Box
                            </Button>
                        )}

                        <Button
                            className="greyButton mar-top-5 edit-multi-item"
                            size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                            disabled={selectedComicIds.length === 0}
                            onClick={() => setShowMarkAsSold(true)}
                        >
                            Mark as Sold
                        </Button>
                        <Button
                            className="greyButton mar-top-5 edit-multi-item"
                            size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                            disabled={selectedComicIds.length === 0}
                            onClick={() => setShowAssignConditionModal(true)}
                        >
                            Assign Grade
                        </Button>

                        <Button
                            className="greyButton mar-top-5 edit-multi-item"
                            size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                            disabled={selectedComicIds.length === 0}
                            onClick={() => setShowAssignPurchasePriceModal(true)}
                        >
                            Assign Purchase Price
                        </Button>
                    </Col>
                </Row>
            )}

            <ClearFiltersBar
                filters={issueParams.filters}
                defaultFilters={defaultFilters}
                issueCount={issueQuery.data ? issueQuery.data.overview.total : 0}
                onClear={() => {
                    if (setSearch) setSearch('');
                    setIssueParams({ ...issueParams, page: 1, filters: defaultFilters });
                }}
            />

            {showListView ? (
                <>
                    {showDesktopView && (
                        <Row className="list-view-header">
                            {showEditMultiple && (
                                <Col span={1}>
                                </Col>
                            )}
                            <Col className={smallButtons ? "width-85px" : "width-135px"}>
                            </Col>
                            <Col className="pad-left-20 flex-grow-1">
                                <div className="flex">
                                    Title
                                    <SortIcon
                                        sort={issueParams.filters.sort}
                                        sortAsc={0}
                                        sortDesc={10}
                                        onChange={(sort) => setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort } })}
                                    />
                                </div>
                            </Col>
                            <Col className="no-wrap" span={smallButtons ? 3 : 2}>
                                <div className="flex-align-center">
                                    Grade
                                    <SortIcon
                                        sort={issueParams.filters.sort}
                                        sortAsc={5}
                                        sortDesc={13}
                                        onChange={(sort) => setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort } })}

                                    />
                                </div>
                            </Col>
                            <Col className="no-wrap" span={smallButtons ? 3 : 2}>
                                <div className="flex-align-center">
                                    Est FMV
                                    {allowUnlimited && (
                                        <SortIcon
                                            sort={issueParams.filters.sort}
                                            sortAsc={1}
                                            sortDesc={14}
                                            onChange={(sort) => setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort } })}

                                        />
                                    )}
                                </div>
                            </Col>
                            <Col className="no-wrap min-width-110" span={3}>
                                <div className="flex-align-center">
                                    Total Cost
                                    {allowUnlimited && (
                                        <SortIcon
                                            sort={issueParams.filters.sort}
                                            sortAsc={16}
                                            sortDesc={17}
                                            onChange={(sort) => setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort } })}

                                        />
                                    )}
                                </div>
                            </Col>
                            <Col className="no-wrap min-width-145" span={3}>
                                <div className="flex-align-center">
                                    Gain/Loss

                                    {allowUnlimited && (
                                        <>
                                            <GainsToggle isDollar={showDollarGains} setIsDollar={setShowDollarGains} />
                                            <SortIcon
                                                sort={issueParams.filters.sort}
                                                sortAsc={showDollarGains ? 9 : 3}
                                                sortDesc={showDollarGains ? 15 : 18}
                                                onChange={(sort) => setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort } })}
                                            />
                                        </>
                                    )}

                                    <Button
                                        onClick={onGainLossInfoClick}
                                        className="info-icon"
                                        type="link"
                                        icon={<InfoIcon className={`gain-loss-icon`} />}
                                    />
                                </div>
                            </Col>
                            <Col className="no-wrap" span={1}>
                                <div className="text-center">Notes</div>
                            </Col>
                            {!smallButtons && (
                                <Col className="no-wrap" span={2}>
                                    <div className="text-center">Box</div>
                                </Col>
                            )}
                            <Col className="no-wrap" span={2}>
                                <div className="text-center">Actions</div>
                            </Col>
                        </Row>
                    )}

                    <div className="mar-left-5 mar-right-5">
                        <IssueGrid
                            isListGrid={true}
                            isLoading={issueQuery.isLoading || issueQuery.isFetching}
                            data={issueQuery.data}
                            noDataMessage="Use the + Add Button Above To Find And Add Comics to Your Collection"
                            IssueRender={(props) =>
                                (<>
                                    <MyCollectionListItem
                                        {...props}
                                        toggleSelected={toggleSelected}
                                        onClick={onIssueClick}
                                        onSetBoxImage={onSetBoxImage}
                                        setShowMarkAsSoldWithIssue={setShowMarkAsSoldWithIssue}
                                        setShowAssignPurchasePriceModalWithIssue={setShowAssignPurchasePriceModalWithIssue}
                                        windowDimensions={windowDimensions}
                                        getBoxName={getBoxName}
                                        removeIssueFromBox={removeIssueFromBox}
                                    />
                                </>)}
                            getItemProps={(item) => ({
                                key: item.collection_item_id,
                                showEditMultiple,
                                isSelected: selectedComicIds.indexOf(item.collection_item_id) !== -1,
                                showPriceInfo: showValueRibbon,
                                showDesktopView,
                                allowUnlimited,
                                showDollarGains: showDesktopView ?
                                    showDollarGains :
                                    showDollarGains && !(issueParams.filters.sort === 3 || issueParams.filters.sort === 9),
                                boxId: showBox ? showBox.id : undefined,
                            })}
                            paginationType={PAGINATION_PAGES}
                            onPaginationChange={(page => {
                                if (issueParams && issueParams.page !== page) {
                                    scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                        setIssueParams({...issueParams, page: page});
                                    });
                                }
                            })}
                            onPaginationSizeChange={(current: number, perPage: number) => {
                                if (issueParams && issueParams.perPage !== perPage) {
                                    scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                        setIssueParams({
                                            ...issueParams,
                                            page: 1,
                                            perPage: perPage,
                                        });
                                        setItemCount(perPage);
                                    });
                                }
                            }}
                        />
                    </div>
                </>
            ) : (
                <div className={'mar-5 issue-grid-container ' + (showEditMultiple ? 'border-top ' : '')}>
                    <IssueGrid
                        isLoading={issueQuery.isLoading || issueQuery.isFetching}
                        data={issueQuery.data}
                        noDataMessage="Use the + Add Button Above To Find And Add Comics to Your Collection"
                        IssueRender={(props) => (
                            <>
                                <MyCollectionGridItem
                                    {...props}
                                    onClick={onIssueClick}
                                    toggleSelected={toggleSelected}
                                    windowDimensions={windowDimensions}
                                />
                            </>)}
                        getItemProps={(item) => ({
                            key: item.collection_item_id,
                            showEditMultiple,
                            isSelected: selectedComicIds.indexOf(item.collection_item_id) !== -1,
                            showValueRibbon,
                            showDesktopView,
                        })}
                        paginationType={PAGINATION_PAGES}
                        onPaginationChange={(page => {
                            if (issueParams && issueParams.page !== page) {
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setIssueParams({...issueParams, page: page});
                                });
                            }
                        })}
                        onPaginationSizeChange={(current: number, perPage: number) => {
                            if (issueParams && issueParams.perPage !== perPage) {
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        perPage: perPage,
                                    });
                                    setItemCount(perPage);
                                });
                            }
                        }}
                    />

                </div>
            )
            }

            <AddEditComicDrawer issue={editIssue} visible={!!editIssue} onClose={() => setEditIssue(null)} />

            <MarkAsSoldDrawer selectedIssues={selectedIssues} visible={showMarkAsSold} onClose={() => setShowMarkAsSold(false)} />

            <AssignConditionModal
                isModalOpen={showAssignConditionModal}
                onCancel={() => setShowAssignConditionModal(false)}
                selectedComicIds={selectedComicIds}
            />

            <AssignLocationModal
                isModalOpen={showAssignLocationModal}
                onCancel={() => setShowAssignLocationModal(false)}
                selectedComicIds={selectedComicIds}
            />

            <AssignPurchasePriceModal
                isModalOpen={showAssignPurchasePriceModal}
                onCancel={() => setShowAssignPurchasePriceModal(false)}
                selectedComicIds={selectedComicIds}
                getItem={(id) => issueQuery?.data?.issues?.find(issue => issue.collection_item_id === id)}
            />
        </>
    );
};

export default MyCollectionIssues;